import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  get_User_Data,
  get_Trade_History,
  get_PnL_Data,
  get_EQuityCurveData,
  get_DrapDownData,
  get_FiveMostProfitTrade,
  get_FiveMostLossTrade,
  getStrategyType,
  GetClientService,
  getChartingStrategyTag,
} from "../../CommonAPI/Admin";
import { GetAllSubadminClient } from "../../CommonAPI/SubAdmin";
import GridExample from "../../../ExtraComponent/CommanDataTable";
import {
  getStrategyTagApi,
  ChartingPlatformSegment,
} from "../../CommonAPI/User";
import DatePicker from "react-datepicker";
import "ag-charts-enterprise";
import ApexCharts from "react-apexcharts";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import {
  columns7,
  columns6,
  columns5,
  columns3,
  columns2,
  columns1,
  columns,
} from "./TradeHistoryColumn";
import NoDataFound from "../../../ExtraComponent/NoDataFound";
import DrawdownChartComponent from "../../admin/AdvanceChart/DrawdownChartComponent";
import ProfitAndLossGraph from "../../admin/AdvanceChart/ProfitAndLossGraph";
import ChartComponent from "../../admin/AdvanceChart/ChartComponent";
import Content from "../../../ExtraComponent/Content";

// Constants
const STRATEGY_TYPES = {
  CHARTING: "ChartingPlatform",
  SCALPING: "Scalping",
  OPTION: "Option Strategy",
  PATTERN: "Pattern",
};

const SECTIONS = {
  OVERVIEW: "overview",
  PNL_ANALYSIS: "pnlAnalysis",
  EQUITY: "equity",
  DRAWDOWN: "drawdown",
  TRADES: "trades",
};

// Utility functions
const convertDateFormat = (date) => {
  if (!date) return "";
  const dateObj = new Date(date);
  return `${dateObj.getFullYear()}.${String(dateObj.getMonth() + 1).padStart(
    2,
    "0"
  )}.${String(dateObj.getDate()).padStart(2, "0")}`;
};

const getDefaultDates = () => {
  const currentDate = new Date();
  const formattedDate = `${currentDate.getFullYear()}.${String(
    currentDate.getMonth() + 1
  ).padStart(2, "0")}.${String(currentDate.getDate()).padStart(2, "0")}`;

  const tomorrow = new Date(currentDate);
  tomorrow.setDate(currentDate.getDate() + 1);
  const defaultToDate = `${tomorrow.getFullYear()}.${String(
    tomorrow.getMonth() + 1
  ).padStart(2, "0")}.${String(tomorrow.getDate()).padStart(2, "0")}`;

  return { formattedDate, defaultToDate };
};

const getChartOptions = (data, type) => ({
  chart: { type: "pie" },
  labels: data.map((t) => t.ETime?.split(" ")[1]?.substring(0, 5) || "N/A"),
  colors: [
    "#FF5733",
    "#33FF57",
    "#3357FF",
    "#FF33A1",
    "#F3FF33",
    "#33FFF3",
    "#A133FF",
    "#FF8C33",
    "#33FF8C",
    "#8C33FF",
  ],
  legend: { position: "bottom" },
  dataLabels: { enabled: true },
  tooltip: {
    y: {
      formatter: (value) => `₹${value.toFixed(2)}`,
    },
  },
});

const Tradehistory = () => {
  const sectionRefs = useRef({});
  const userName = localStorage.getItem("name");
  const storedStrategyType = sessionStorage.getItem("StrategyType");
  const storedUsername = sessionStorage.getItem("Username");

  // Date setup
  const { formattedDate, defaultToDate } = useMemo(() => getDefaultDates(), []);

  // State management
  const [selectStrategyType, setStrategyType] = useState(
    storedStrategyType || STRATEGY_TYPES.CHARTING
  );
  const [strategyNames, setStrategyNames] = useState([]);
  const [tradeHistory, setTradeHistory] = useState({ data: [], data1: [] });
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [toDate, setToDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [showReportSections, setShowReportSections] = useState(false);
  const [groupData, setGroupData] = useState({ loading: true, data: [] });
  const [selectGroup, setSelectGroup] = useState(storedUsername || "");
  const [activeTab, setActiveTab] = useState("Cash");
  const [strategyTagOptions, setStrategyTagOptions] = useState([]);
  const [selectedStrategyTag, setSelectedStrategyTag] = useState("All");
  const [chartingSegmentOption, setChartingSegmentOption] = useState([]);
  const [multiplier, setMultiplier] = useState(1);
  const [clientName, setClientName] = useState({ loading: true, data: [] });
  const [openSections, setOpenSections] = useState({});

  const [loadedSections, setLoadedSections] = useState({
    overview: false,
    pnlAnalysis: false,
    equity: false,
    drawdown: false,
    trades: false,
  });

  // Data states
  const [allTradeData, setAllTradeData] = useState({ data: [], Overall: [] });
  const [pnlData, setPnlData] = useState({ data: [] });
  const [equityCurveDetails, setEquityCurveDetails] = useState({ data: [] });
  const [dropDownData, setDropDownData] = useState({ data: [] });
  const [fiveLossTrade, setFiveLossTrade] = useState({ data: [] });
  const [fiveProfitTrade, setFiveProfitTrade] = useState({ data: [] });
  const [chartingData, setChartingData] = useState([]);

  // Fetch functions with useCallback
  const fetchStrategyTypes = useCallback(async () => {
    try {
      const res = await getStrategyType({ name: selectGroup });
      setStrategyNames(res?.Data || []);
    } catch (error) {
      console.error("Error fetching strategy types:", error);
      setStrategyNames([]);
    }
  }, [selectGroup]);

  const fetchGroupDetails = useCallback(async () => {
    try {
      const response = await GetClientService();
      setGroupData({
        loading: false,
        data: response?.Status ? response.Data || [] : [],
      });
    } catch (error) {
      console.error("Error fetching group data:", error);
      setGroupData({ loading: false, data: [] });
    }
  }, []);

  const fetchChartingSegments = useCallback(async () => {
    try {
      const response = await ChartingPlatformSegment();
      setChartingSegmentOption(response?.Segment || []);
    } catch (error) {
      console.error("Error fetching charting segments:", error);
      setChartingSegmentOption([]);
    }
  }, []);

  const fetchStrategyTags = useCallback(async () => {
    try {
      let response;
      if (selectGroup === "Admin") {
        response = await getChartingStrategyTag();
        setStrategyTagOptions(
          response?.data?.map((item) => item.Strategytag) || []
        );
      } else {
        response = await getStrategyTagApi(selectGroup);
        setStrategyTagOptions(response?.StrategyTag || []);
      }
    } catch (error) {
      console.error("Error fetching strategy tags:", error);
      setStrategyTagOptions([]);
    }
  }, [selectGroup]);

  const fetchAllSubadmin = useCallback(async () => {
    try {
      const response = await GetAllSubadminClient({ userName });
      setClientName({
        loading: false,
        data: response?.Status ? response.Data : [],
      });
      setSelectGroup(
        storedUsername || (response?.Status && response.Data[0]?.Username) || ""
      );
    } catch (error) {
      console.error("Error fetching subadmin:", error);
      setClientName({ loading: false, data: [] });
    }
  }, [userName]);

  const fetchTradeHistory = useCallback(async () => {
    try {
      const dataType =
        selectStrategyType === STRATEGY_TYPES.SCALPING
          ? "NewScalping"
          : selectStrategyType;

      const response = await get_User_Data({
        Data: dataType,
        Username: selectGroup,
      });

      setTradeHistory(
        response?.Status
          ? { data: response?.Data || [], data1: response?.NewScalping || [] }
          : { data: [], data1: [] }
      );
    } catch (err) {
      console.error("Error fetching trade history:", err);
      setTradeHistory({ data: [], data1: [] });
    }
  }, [selectStrategyType, selectGroup]);

  const buildRequestParams = useCallback(
    (includeRowData = false) => {
      const baseParams = {
        Username: selectGroup,
        From_date: convertDateFormat(fromDate || formattedDate),
        To_date: convertDateFormat(toDate || defaultToDate),
      };

      if (selectStrategyType === STRATEGY_TYPES.CHARTING) {
        return {
          ...baseParams,
          MainStrategy: STRATEGY_TYPES.CHARTING,
          Strategy: activeTab,
          Symbol: "",
          ETPattern:
            activeTab === "Cash"
              ? "NSE"
              : activeTab.includes("NFO")
              ? "NFO"
              : activeTab.includes("MCX")
              ? "MCX"
              : "BFO",
          Timeframe: "",
          Group: "",
          TradePattern: "",
          PatternName: "",
          mult: multiplier,
        };
      }

      if (!includeRowData || !selectedRowData) return null;

      return {
        ...baseParams,
        MainStrategy:
          selectStrategyType === STRATEGY_TYPES.SCALPING
            ? selectedRowData.ScalpType === "Multi_Conditional"
              ? "NewScalping"
              : selectStrategyType
            : selectStrategyType,
        Strategy:
          selectStrategyType === STRATEGY_TYPES.SCALPING
            ? selectedRowData.Targetselection
            : selectStrategyType === STRATEGY_TYPES.OPTION
            ? selectedRowData.STG
            : selectStrategyType === STRATEGY_TYPES.PATTERN
            ? selectedRowData.TradePattern
            : "Cash",
        Symbol: [STRATEGY_TYPES.SCALPING, STRATEGY_TYPES.PATTERN].includes(
          selectStrategyType
        )
          ? selectedRowData.Symbol
          : selectStrategyType === STRATEGY_TYPES.OPTION
          ? selectedRowData.IName
          : "",
        ETPattern:
          selectStrategyType === STRATEGY_TYPES.SCALPING
            ? selectedRowData.TType
            : selectStrategyType === STRATEGY_TYPES.OPTION
            ? selectedRowData.Targettype
            : selectStrategyType === STRATEGY_TYPES.PATTERN
            ? selectedRowData.Pattern
            : "",
        Group: [STRATEGY_TYPES.SCALPING, STRATEGY_TYPES.OPTION].includes(
          selectStrategyType
        )
          ? selectedRowData.GroupN
          : "",
        Timeframe:
          selectStrategyType === STRATEGY_TYPES.PATTERN
            ? selectedRowData.TimeFrame
            : "",
        TradePattern: "",
        PatternName: "",
        InitialDeposite: 0,
        Dataid: selectedRowData._id,
      };
    },
    [
      selectStrategyType,
      selectGroup,
      fromDate,
      toDate,
      formattedDate,
      defaultToDate,
      activeTab,
      multiplier,
      selectedRowData,
    ]
  );

  const getChartingSegmentData = useCallback(async () => {
    try {
      setLoadedSections({
        overview: true,
        pnlAnalysis: false,
        equity: false,
        drawdown: false,
        trades: false,
      });

      const params = buildRequestParams(false);
      if (!params) return;

      const res = await get_Trade_History(params);
      setChartingData(res?.data || []);
      setAllTradeData({
        data: res?.data || [],
        Overall: res?.Overall || [],
      });
      setShowReportSections(true);
    } catch (error) {
      console.error("Error in getChartingSegmentData", error);
    }
  }, [buildRequestParams]);

  const loadSectionData = useCallback(
    async (section) => {
      if (loadedSections[section]) return;

      try {
        if (section === SECTIONS.OVERVIEW) {
          setLoadedSections((prev) => ({ ...prev, [section]: true }));
          return;
        }

        const params = buildRequestParams(
          selectStrategyType !== STRATEGY_TYPES.CHARTING
        );
        if (!params) return;

        switch (section) {
          case SECTIONS.PNL_ANALYSIS:
            const pnlRes = await get_PnL_Data(params);
            setPnlData({ data: pnlRes?.Barchart || [] });
            break;

          case SECTIONS.EQUITY:
            const equityRes = await get_EQuityCurveData(params);
            setEquityCurveDetails({ data: equityRes?.Equitycurve || [] });
            break;

          case SECTIONS.DRAWDOWN:
            const drawdownRes = await get_DrapDownData(params);
            setDropDownData({ data: drawdownRes?.Drawdown || [] });
            break;

          case SECTIONS.TRADES:
            const [lossRes, profitRes] = await Promise.all([
              get_FiveMostLossTrade(params),
              get_FiveMostProfitTrade(params),
            ]);
            setFiveLossTrade({ data: lossRes?.fivelosstrade || [] });
            setFiveProfitTrade({ data: profitRes?.fiveprofittrade || [] });
            break;

          default:
            break;
        }

        setLoadedSections((prev) => ({ ...prev, [section]: true }));
      } catch (error) {
        console.error(`Error loading ${section} data:`, error);
        Swal.fire({
          icon: "error",
          title: `Failed to load ${section} data`,
          text: error.message,
          confirmButtonColor: "#1ccc8a",
        });
      }
    },
    [loadedSections, buildRequestParams, selectStrategyType]
  );

  // Effects
  useEffect(() => {
    fetchGroupDetails();
    fetchAllSubadmin();
    fetchChartingSegments();
  }, [fetchGroupDetails, fetchAllSubadmin, fetchChartingSegments]);

  useEffect(() => {
    if (selectGroup) {
      fetchStrategyTypes();
      fetchStrategyTags();
    }
  }, [selectGroup, fetchStrategyTypes, fetchStrategyTags]);

  useEffect(() => {
    if (selectStrategyType && selectGroup) {
      fetchTradeHistory();
    }
  }, [selectStrategyType, selectGroup, fromDate, toDate, fetchTradeHistory]);

  useEffect(() => {
    if (selectStrategyType === STRATEGY_TYPES.CHARTING) {
      getChartingSegmentData();
      setOpenSections({ overview: true });
    } else {
      setShowReportSections(false);
      setOpenSections({});
    }
  }, [
    activeTab,
    selectGroup,
    selectStrategyType,
    fromDate,
    toDate,
    multiplier,
    getChartingSegmentData,
  ]);

  // Event handlers
  const handleRowSelect = useCallback((rowData) => {
    setSelectedRowData(rowData);
    setOpenSections({});
    setShowReportSections(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedRowData) {
      Swal.fire({
        icon: "warning",
        title: "Please select a row first!",
        confirmButtonColor: "#1ccc8a",
      });
      return;
    }

    setLoadedSections({
      overview: false,
      pnlAnalysis: false,
      equity: false,
      drawdown: false,
      trades: false,
    });

    try {
      const params = buildRequestParams(true);
      if (!params) return;

      const tradeRes = await get_Trade_History(params);

      if (!tradeRes?.data || tradeRes.data.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No Record Found",
          text: "No data available for the selected criteria.",
          confirmButtonColor: "#1ccc8a",
        });
        return;
      }

      setAllTradeData({
        data: tradeRes.data || [],
        Overall: tradeRes.Overall || [],
      });
      setShowReportSections(true);
      setOpenSections({ overview: true });
      setLoadedSections((prev) => ({ ...prev, overview: true }));

      setTimeout(() => {
        sectionRefs.current[SECTIONS.OVERVIEW]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed to load data",
        text: error.message,
        confirmButtonColor: "#1ccc8a",
      });
    }
  }, [selectedRowData, buildRequestParams]);

  const toggleSection = useCallback(
    async (section) => {
      const isOpen = openSections[section];

      if (!isOpen) {
        setOpenSections({ [section]: true });
        await loadSectionData(section);

        setTimeout(() => {
          sectionRefs.current[section]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 200);
      } else {
        setOpenSections((prev) => ({ ...prev, [section]: false }));
      }
    },
    [openSections, loadSectionData]
  );

  const getColumnsForStrategy = useCallback(() => {
    switch (selectStrategyType) {
      case STRATEGY_TYPES.SCALPING:
        return columns();
      case STRATEGY_TYPES.OPTION:
        return columns1();
      case STRATEGY_TYPES.PATTERN:
        return columns2();
      case STRATEGY_TYPES.CHARTING:
        return columns7();
      default:
        return columns();
    }
  }, [selectStrategyType]);

  // Render helpers
  const ReportSection = ({ title, section, children }) => {
    const isOpen = openSections[section] || false;
    const sectionRef = (el) => (sectionRefs.current[section] = el);

    const renderButtonContent = () => {
      if (!isOpen) {
        return "Show Data";
      }
      return loadedSections[section] ? (
        "Hide Data"
      ) : (
        <span
          className="spinner-border spinner-border-sm me-1"
          role="status"
          aria-hidden="true"
        />
      );
    };

    return (
      <div className="card mb-3" ref={sectionRef}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="m-0">{title}</h5>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => toggleSection(section)}
          >
            {renderButtonContent()}
          </button>
        </div>
        {isOpen && (
          <div className="card-body">
            {loadedSections[section] ? (
              children
            ) : (
              <div className="spinner-border text-primary" />
            )}
          </div>
        )}
      </div>
    );
  };

  const filteredTradeData = useMemo(() => {
    if (
      selectStrategyType !== STRATEGY_TYPES.CHARTING ||
      selectedStrategyTag === "All"
    ) {
      return allTradeData.data;
    }
    return allTradeData.data.filter(
      (item) => item.StrategyTag === selectedStrategyTag
    );
  }, [allTradeData.data, selectStrategyType, selectedStrategyTag]);

  return (
    <Content
      Page_title="📊 Trade History"
      button_status={false}
      backbutton_status={true}
    >
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item active " aria-current="page">
            Trade History
          </li>
        </ol>
      </nav>
      <div className="">
        <div className="">
          {/* Strategy Type Pills */}
          <div className="col-12 mb-3">
            <div className="d-flex report-btns">
              <ul className="nav nav-tabs ">
                {strategyNames.map((type, index) => (
                  <li className="nav-item" key={index}>
                    <a
                      className={`nav-link ${
                        selectStrategyType === type ? "active" : ""
                      }`}
                      onClick={() => {
                        setStrategyType(type);
                        sessionStorage.setItem("StrategyType", type);
                      }}
                    >
                      {type}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {/* Filters Section */}

          <div style={{ width: "100%", marginBottom: "24px" }}>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                padding: "16px",
                display: "flex",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              {/* Strategy Tag */}
              {selectStrategyType === STRATEGY_TYPES.CHARTING && (
                <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "600",
                      color: "#333",
                    }}
                  >
                    Strategy Tag
                  </label>
                  <select
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      fontSize: "0.95rem",
                    }}
                    value={selectedStrategyTag}
                    onChange={(e) => setSelectedStrategyTag(e.target.value)}
                  >
                    <option value="All">All</option>
                    {strategyTagOptions.map((tag, idx) => (
                      <option key={idx} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* From Date */}
              <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Select From Date
                </label>
                <DatePicker
                  selected={fromDate || formattedDate}
                  onChange={setFromDate}
                  dateFormat="dd/MM/yyyy"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    fontSize: "0.95rem",
                  }}
                  className="form-control"
                />
              </div>

              {/* To Date */}
              <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Select To Date
                </label>
                <DatePicker
                  selected={toDate || defaultToDate}
                  onChange={setToDate}
                  dateFormat="dd/MM/yyyy"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    fontSize: "0.95rem",
                  }}
                  className="form-control"
                />
              </div>

              {/* Select User */}
              <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Select User
                </label>
                <select
                  required
                  onChange={(e) => {
                    setSelectGroup(e.target.value);
                    sessionStorage.setItem("Username", e.target.value);
                  }}
                  value={selectGroup}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    fontSize: "0.95rem",
                  }}
                >
                  <option value="">Select User</option>
                  {clientName.data?.map((item) => (
                    <option key={item.Username} value={item.Username}>
                      {item.Username}
                    </option>
                  ))}
                </select>
              </div>

              {/* Multiplier */}
              {selectStrategyType === STRATEGY_TYPES.CHARTING && (
                <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "600",
                      color: "#333",
                    }}
                  >
                    Multiplier
                  </label>
                  <input
                    type="number"
                    placeholder="Enter multiplier"
                    value={multiplier}
                    onChange={(e) => setMultiplier(Number(e.target.value) || 1)}
                    min="1"
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "6px",
                      border: "1px solid #ccc",
                      fontSize: "0.95rem",
                    }}
                  />
                </div>
              )}
            </div>

            {/* Charting Segment Pills */}
            {selectStrategyType === STRATEGY_TYPES.CHARTING && (
              <div style={{ marginTop: "12px" }}>
                <ul
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    listStyle: "none",
                    padding: "6px",
                    margin: 0,
                    // boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    borderRadius: "6px",
                  }}
                >
                  {chartingSegmentOption.map((segment) => (
                    <li key={segment} style={{ margin: "6px" }}>
                      <button
                        onClick={() => setActiveTab(segment)}
                        style={{
                          padding: "7px 14px",
                          fontSize: "0.97rem",
                          borderRadius: "6px",
                          border: "none",
                          cursor: "pointer",
                          minWidth: "80px",
                          backgroundColor:
                            activeTab === segment ? "var(--button-color)" : "#e9ecef",
                          color: activeTab === segment ? "#fff" : "#333",
                          transition: "0.3s all",
                        }}
                      >
                        {segment}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Data Grid */}
          {selectStrategyType !== STRATEGY_TYPES.CHARTING && (
            <div className="mb-4">
              {tradeHistory.data?.length > 0 ? (
                <GridExample
                  columns={getColumnsForStrategy()}
                  data={tradeHistory.data}
                  onRowSelect={handleRowSelect}
                  checkBox={true}
                />
              ) : (
                <NoDataFound />
              )}
            </div>
          )}

          {/* Generate Button */}
          {selectStrategyType !== STRATEGY_TYPES.CHARTING && (
            <div className="">
              <button
                className="addbtn btn hoverNone"
                onClick={handleSubmit}
                disabled={!selectedRowData}
              >
                📜 Generate History
              </button>
            </div>
          )}

          {/* Report Sections */}
          {showReportSections && (
            <div className="mt-5">
              {/* Overview */}
              <ReportSection
                title="Total Profit/Loss Overview"
                section={SECTIONS.OVERVIEW}
              >
                {allTradeData?.Overall?.length > 0 ? (
                  <>
                    <div
                      className="pnl-overview"
                      style={{
                        color: "#fff",
                        padding: "20px",
                        borderRadius: "8px",
                        textAlign: "center",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        marginBottom: "20px",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: "1.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        💰 Total PnL: ₹
                        <span
                          className="badge ms-2"
                          style={{
                            backgroundColor:
                              allTradeData.Overall[0]?.PnL > 0
                                ? "#008000"
                                : "#B22222",
                            color: "white",
                            padding: "8px 15px",
                            borderRadius: "5px",
                            fontSize: "1.25rem",
                            fontWeight: "bold",
                          }}
                        >
                          {allTradeData.Overall[0]?.PnL?.toFixed(2) || "0.00"}
                        </span>
                      </h4>
                    </div>
                    <GridExample
                      columns={columns3(selectStrategyType)}
                      data={filteredTradeData}
                      checkBox={false}
                    />
                  </>
                ) : (
                  <NoDataFound />
                )}
              </ReportSection>

              {/* PnL Analysis */}
              <ReportSection
                title="Profit/Loss Analysis"
                section={SECTIONS.PNL_ANALYSIS}
              >
                {pnlData.data?.length > 0 ? (
                  <ProfitAndLossGraph data={pnlData.data} />
                ) : (
                  <NoDataFound />
                )}
              </ReportSection>

              {/* Equity Curve */}
              <ReportSection
                title="Equity Curve Analysis"
                section={SECTIONS.EQUITY}
              >
                {equityCurveDetails.data?.length > 0 ? (
                  <>
                    <div style={{ height: "350px", overflow: "hidden" }}>
                      <ChartComponent data={equityCurveDetails.data} />
                    </div>
                    <GridExample
                      columns={columns5(selectStrategyType)}
                      data={equityCurveDetails.data}
                      checkBox={false}
                    />
                  </>
                ) : (
                  <NoDataFound />
                )}
              </ReportSection>

              {/* Drawdown */}
              <ReportSection
                title="Drawdown Analysis"
                section={SECTIONS.DRAWDOWN}
              >
                {dropDownData.data?.length > 0 ? (
                  <>
                    <div style={{ height: "350px", overflow: "hidden" }}>
                      <DrawdownChartComponent data={dropDownData.data} />
                    </div>
                    <GridExample
                      columns={columns6()}
                      data={dropDownData.data}
                      checkBox={false}
                    />
                  </>
                ) : (
                  <NoDataFound />
                )}
              </ReportSection>

              {/* Top Trades */}
              <ReportSection
                title="Top Trades Analysis"
                section={SECTIONS.TRADES}
              >
                {fiveProfitTrade.data?.length > 0 ||
                fiveLossTrade.data?.length > 0 ? (
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header bg-success text-white">
                          Top Profitable Trades
                        </div>
                        <div className="card-body">
                          {fiveProfitTrade.data?.length > 0 ? (
                            <ApexCharts
                              options={getChartOptions(
                                fiveProfitTrade.data,
                                "Profit"
                              )}
                              series={fiveProfitTrade.data.map((t) => t.PnL)}
                              type="pie"
                              height={350}
                            />
                          ) : (
                            <NoDataFound />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header bg-danger text-white">
                          Top Loss-making Trades
                        </div>
                        <div className="card-body">
                          {fiveLossTrade.data?.length > 0 ? (
                            <ApexCharts
                              options={getChartOptions(
                                fiveLossTrade.data,
                                "Loss"
                              )}
                              series={fiveLossTrade.data.map((t) =>
                                Math.abs(t.PnL)
                              )}
                              type="pie"
                              height={350}
                            />
                          ) : (
                            <NoDataFound />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <NoDataFound />
                )}
              </ReportSection>
            </div>
          )}
        </div>
      </div>
    </Content>
  );
};

export default Tradehistory;
